import type { Vault, PlatformSeries, PlatformStats } from 'src/pages/admin/services/vaultService';

import { formatCount, formatNav, formatSignedUsdc, formatUsdc } from '../vaults/helpers';
import { ENGINE_ERROR_KEYS } from '../params/helpers';
import { type ApiErrorInfo } from 'src/pages/admin/utils/decimal-input';
import { DASHBOARD_NS as NS, TOP_VAULTS } from './constants';

/** What a card shows while its figure is unknown (loading, failed): never a 0 that reads as an empty platform. */
export const EMPTY = '—';

/**
 * The viewer's zone, sent as `timeZone` (C-03 v4, C-06 §14.2): the browser's IANA name, or an offset when the runtime
 * names none. "Today" and every day on the charts are days in this zone.
 */
export const viewerTimeZone = (): string => {
  try {
    const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (name) return name;
  } catch {
    // fall through to the offset
  }
  const minutes = -new Date().getTimezoneOffset();
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};

export interface StatCards {
  tvl: string;
  holders: string;
  netSubscriptions: string;
  managerFees: string;
  /** Vaults left out of the TVL because they have no cut-off yet: "+N not yet valued" when above 0. */
  unvalued: number;
  /** The oldest cut-off summed, as an ISO instant for formatInstant; null when none or unknown. */
  valuedAt: string | null;
  /** The sign of the net subscriptions, for the colour: -1, 0, 1; 0 while unknown. */
  netSign: -1 | 0 | 1;
}

const NO_CARDS: StatCards = {
  tvl: EMPTY,
  holders: EMPTY,
  netSubscriptions: EMPTY,
  managerFees: EMPTY,
  unvalued: 0,
  valuedAt: null,
  netSign: 0,
};

/**
 * The four cards. Without stats (loading or failed) every figure is "—", never 0. A day with no activity reads as the
 * real zero the engine answered. Money is cut on the string at 2 dp, rounded DOWN (formatUsdc), never through a float.
 */
export const statCards = (stats: PlatformStats | null): StatCards => {
  if (!stats) return NO_CARDS;
  const net = stats.netSubscriptions.trim();
  return {
    tvl: formatUsdc(stats.totalValueLocked),
    holders: formatCount(stats.holderCount),
    netSubscriptions: formatSignedUsdc(net),
    managerFees: formatUsdc(stats.managerFees),
    unvalued: stats.unvaluedVaultCount,
    valuedAt: stats.valuedAt,
    netSign: /[1-9]/.test(net) ? (net.startsWith('-') ? -1 : 1) : 0,
  };
};

/** A decimal string for a chart axis only (display, never a ledger figure): null stays null so the line has a gap. */
const plot = (value: string | null): number | null => {
  if (value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** `2026-10-11` → `10-11`: the chart axis label. */
export const axisLabel = (date: string): string => date.slice(5);

export interface ChartLine {
  name: string;
  data: (number | null)[];
}

export const holdersLine = (series: PlatformSeries): ChartLine[] => [
  { name: '', data: series.days.map((d) => d.holderCount) },
];

export const feesLine = (series: PlatformSeries): ChartLine[] => [
  { name: '', data: series.days.map((d) => plot(d.managerFees)) },
];

/** The NAV lines of the chosen vaults, in the order given. A day without a cut-off is null: a gap, never 0. */
export const navLines = (series: PlatformSeries, chosen: readonly string[]): ChartLine[] => {
  const byId = new Map(series.vaultNav.map((v) => [v.vaultId, v]));
  return chosen.flatMap((id) => {
    const v = byId.get(id);
    return v ? [{ name: v.name, data: v.points.map((p) => plot(p.nav)) }] : [];
  });
};

/** −1, 0 or 1 comparing two non-negative 6-dp decimal strings, read without a float. */
const compareDecimal = (a: string, b: string): number => {
  const [ai, af = ''] = a.split('.');
  const [bi, bf = ''] = b.split('.');
  const ni = ai.replace(/^0+(?=\d)/, '');
  const nj = bi.replace(/^0+(?=\d)/, '');
  if (ni.length !== nj.length) return ni.length < nj.length ? -1 : 1;
  if (ni !== nj) return ni < nj ? -1 : 1;
  const x = af.padEnd(6, '0');
  const y = bf.padEnd(6, '0');
  return x === y ? 0 : x < y ? -1 : 1;
};

/**
 * The vault ids the NAV chart draws by default: the top TOP_VAULTS by TVL (the list's `valuation.totalAssets`, the
 * newest cut-off), then by id. A vault with no cut-off ranks after every valued one, in the series' own order (official
 * first), so the chart still fills when the list is not available. Ids not in the series are ignored.
 */
export const topVaultIds = (
  series: PlatformSeries,
  vaults: readonly Pick<Vault, 'vaultId' | 'valuation'>[],
  limit = TOP_VAULTS
): string[] => {
  const tvl = new Map<string, string | null>(
    vaults.map((v) => [v.vaultId, v.valuation ? v.valuation.totalAssets : null])
  );
  const order = new Map(series.vaultNav.map((v, i) => [v.vaultId, i]));
  return series.vaultNav
    .map((v) => v.vaultId)
    .sort((a, b) => {
      const ta = tvl.get(a) ?? null;
      const tb = tvl.get(b) ?? null;
      if (ta !== null && tb !== null) {
        const c = compareDecimal(tb, ta);
        if (c !== 0) return c;
        return a < b ? -1 : a > b ? 1 : 0;
      }
      if (ta !== null) return -1;
      if (tb !== null) return 1;
      return (order.get(a) ?? 0) - (order.get(b) ?? 0);
    })
    .slice(0, limit);
};

/**
 * The fee bar's tooltip: the day's fee read from the engine's own string by the bar's index and cut at 2 dp rounded
 * DOWN like the card (money always rounds down), never through the plotted float: "1.999999" → "1.99".
 */
export const feeTooltip = (series: PlatformSeries | null, index: number | undefined): string => {
  const day = series && index !== undefined ? series.days[index] : undefined;
  return day ? formatUsdc(day.managerFees) : EMPTY;
};

/**
 * The viewer's NAV selection after the series was read again: kept (a Refresh must not undo it) unless a picked vault
 * is no longer in the series, which resets it to the default. A failed read (no series) keeps it too.
 */
export const keepSelection = (
  picked: string[] | null,
  series: PlatformSeries | null
): string[] | null => {
  if (!picked || !series) return picked;
  const known = new Set(series.vaultNav.map((v) => v.vaultId));
  return picked.every((id) => known.has(id)) ? picked : null;
};

/** NAV at 4 dp for the chart tooltip (6 internal, 4 displayed). */
export const navTooltip = (value: number | null): string =>
  value === null ? EMPTY : formatNav(value.toFixed(6));

/** The banner for a failed read: one translated line; the raw upstream text is not shown. */
export const dashboardErrorMessage = (
  info: ApiErrorInfo,
  translate: (key: string) => string
): string => translate((info.code && ENGINE_ERROR_KEYS[info.code]) || `${NS}.loadFailed`);
